import { useState, useMemo, useId } from 'react';
import styles from './TerminalMultiSelect.module.css';

export interface Terminal {
  id: number;
  name: string;
  /** מזהה אלפא-נומרי (למשל 'TRM-001') */
  code?: string;
  /** כתובת/שם מיקום */
  location?: string;
  active?: boolean;
  /** לסינון לפי קבוצה */
  groupId?: number;
  /** אם showAssignedBadge=true, מציג ליד הכרטיס */
  assignedTo?: string;
}

export type TerminalMultiSelectVariant = 'list' | 'table' | 'grid';

export interface TerminalMultiSelectProps {
  availableTerminals: Terminal[];
  /** Set של IDs מסומנים */
  selectedIds: Set<number>;
  onChange: (ids: Set<number>) => void;
  /** מציג שדה חיפוש */
  searchable?: boolean;
  /** מציג רק מסופים ב-groupId הזה */
  filterByGroup?: number;
  /** badge "שייך ל-X" */
  showAssignedBadge?: boolean;
  /** "בחר הכל" / "בטל הכל" / "הפוך" */
  showBulkActions?: boolean;
  /** list=row | table=table | grid=auto-fit */
  variant?: TerminalMultiSelectVariant;
  /** גובה ה-scroll viewport */
  height?: number;
  ariaLabel?: string;
  disabled?: boolean;
  className?: string;
}

const cx = (...classes: Array<string | false | null | undefined>): string =>
  classes.filter(Boolean).join(' ');

/**
 * רכיב בחירת מסופים מרובה — search + bulk actions + counter.
 *
 * **מצב Set** — `selectedIds` הוא Set<number> ל-O(1) toggle. ה-callback מחזיר Set חדש.
 *
 * @example
 * const [selected, setSelected] = useState(new Set());
 *
 * <TerminalMultiSelect
 *   availableTerminals={terminals}
 *   selectedIds={selected}
 *   onChange={setSelected}
 *   ariaLabel="בחירת מסופים להקצאה"
 * />
 */
export const TerminalMultiSelect = ({
  availableTerminals,
  selectedIds,
  onChange,
  searchable = true,
  filterByGroup,
  showAssignedBadge = false,
  showBulkActions = true,
  variant = 'list',
  height = 400,
  ariaLabel = 'בחירת מסופים מרובים',
  disabled = false,
  className,
}: TerminalMultiSelectProps): JSX.Element => {
  const [search, setSearch] = useState('');
  const searchInputId = useId();

  const filtered = useMemo<Terminal[]>(() => {
    let list = availableTerminals;
    if (filterByGroup !== undefined && filterByGroup !== null) {
      list = list.filter((t) => t.groupId === filterByGroup);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((t) => {
        const name = (t.name || '').toLowerCase();
        const code = (t.code || '').toLowerCase();
        const location = (t.location || '').toLowerCase();
        return name.includes(q) || code.includes(q) || location.includes(q);
      });
    }
    return list;
  }, [availableTerminals, search, filterByGroup]);

  const handleToggle = (id: number): void => {
    if (disabled) return;
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onChange(next);
  };

  const handleSelectAll = (): void => {
    if (disabled) return;
    const next = new Set(selectedIds);
    filtered.forEach((t) => next.add(t.id));
    onChange(next);
  };

  const handleDeselectAll = (): void => {
    if (disabled) return;
    const next = new Set(selectedIds);
    filtered.forEach((t) => next.delete(t.id));
    onChange(next);
  };

  const handleInvert = (): void => {
    if (disabled) return;
    const next = new Set(selectedIds);
    filtered.forEach((t) => {
      if (next.has(t.id)) next.delete(t.id);
      else next.add(t.id);
    });
    onChange(next);
  };

  const filteredCount = filtered.length;
  const totalCount = availableTerminals.length;
  const selectedInFiltered = filtered.filter((t) => selectedIds.has(t.id)).length;

  return (
    <div
      className={cx(styles.root, styles[variant], disabled && styles.disabled, className)}
      role="group"
      aria-label={ariaLabel}
      data-testid="terminal-multi-select"
    >
      {(searchable || showBulkActions) && (
        <div className={styles.controls}>
          {searchable && (
            <input
              id={searchInputId}
              type="search"
              className={styles.search}
              placeholder="חיפוש לפי שם / קוד / מיקום..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={disabled}
              aria-label="חיפוש מסופים"
              data-testid="terminal-multi-select-search"
            />
          )}
          {showBulkActions && (
            <div className={styles.bulkActions} role="toolbar" aria-label="פעולות בקבוצה">
              <button
                type="button"
                className={styles.bulkBtn}
                onClick={handleSelectAll}
                disabled={disabled || filteredCount === 0}
                data-testid="terminal-multi-select-select-all"
              >
                בחר הכל
              </button>
              <button
                type="button"
                className={styles.bulkBtn}
                onClick={handleDeselectAll}
                disabled={disabled || selectedInFiltered === 0}
                data-testid="terminal-multi-select-deselect-all"
              >
                בטל הכל
              </button>
              <button
                type="button"
                className={styles.bulkBtn}
                onClick={handleInvert}
                disabled={disabled || filteredCount === 0}
                data-testid="terminal-multi-select-invert"
              >
                הפוך
              </button>
            </div>
          )}
        </div>
      )}

      <div className={styles.counter} data-testid="terminal-multi-select-counter">
        {`${selectedIds.size} מתוך ${totalCount} נבחרו`}
        {(search || filterByGroup !== undefined) && filteredCount !== totalCount && (
          <span className={styles.counterFiltered}>
            {` (${filteredCount} מסוננים)`}
          </span>
        )}
      </div>

      <div
        className={styles.listWrap}
        style={{ maxHeight: height }}
        data-testid="terminal-multi-select-list"
      >
        {filtered.length === 0 ? (
          <div className={styles.emptyState} data-testid="terminal-multi-select-empty">
            לא נמצאו מסופים תואמים
          </div>
        ) : (
          filtered.map((t) => {
            const isSelected = selectedIds.has(t.id);
            return (
              <label
                key={t.id}
                className={cx(
                  styles.item,
                  isSelected && styles.itemSelected,
                  t.active === false && styles.itemInactive
                )}
                data-testid={`terminal-multi-select-item-${t.id}`}
              >
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={isSelected}
                  onChange={() => handleToggle(t.id)}
                  disabled={disabled}
                  data-testid={`terminal-multi-select-checkbox-${t.id}`}
                />
                <div className={styles.itemBody}>
                  <div className={styles.itemTitle}>
                    {t.code && <span className={styles.itemCode}>{t.code}</span>}
                    <span className={styles.itemName}>{t.name}</span>
                    {t.active === false && (
                      <span className={styles.itemInactiveBadge} aria-label="לא פעיל">
                        כבוי
                      </span>
                    )}
                  </div>
                  {t.location && (
                    <div className={styles.itemLocation}>{t.location}</div>
                  )}
                </div>
                {showAssignedBadge && t.assignedTo && (
                  <span
                    className={styles.assignedBadge}
                    data-testid={`terminal-multi-select-assigned-${t.id}`}
                  >
                    שייך ל-{t.assignedTo}
                  </span>
                )}
              </label>
            );
          })
        )}
      </div>
    </div>
  );
};
